import React, { useState, useRef, useEffect } from 'react';
import './AdvancedChat.css';

interface Source {
  text: string;
  source: string;
  section: string;
  subsection?: string;
  score: number;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  hospitalSources?: Source[];
  regulationSources?: Source[];
  gapsIdentified?: string[];
  rewrittenQuery?: string;
  steps?: string[];
  id: string;
}

export const AdvancedChat: React.FC = () => {
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const callRagAdvanced = async (question: string) => {
    const apiUrl = process.env.NODE_ENV === 'production'
      ? 'https://ceaustin117-personal-website-backend.hf.space/rag-advanced'
      : 'http://localhost:7860/rag-advanced';

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    return response.json();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: inputMessage,
      id: Date.now().toString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await callRagAdvanced(inputMessage);

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.answer,
        hospitalSources: response.hospital_sources,
        regulationSources: response.regulation_sources,
        gapsIdentified: response.gaps_identified,
        rewrittenQuery: response.rewritten_query,
        steps: response.steps,
        id: (Date.now() + 1).toString(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: Message = {
        role: 'assistant',
        content: `Error: ${error instanceof Error ? error.message : 'Failed to get response'}`,
        id: (Date.now() + 1).toString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const renderSources = (sources: Source[], title: string, messageId: string, type: 'hospital' | 'regulation') => {
    const sectionId = `${messageId}-${type}`;
    const isExpanded = expandedSections.has(sectionId);

    return (
      <div className="advanced-sources-section">
        <button
          className="sources-toggle"
          onClick={() => toggleSection(sectionId)}
        >
          <span className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
          {title} ({sources.length})
        </button>
        {isExpanded && (
          <div className="sources-list">
            {sources.map((source, idx) => (
              <div key={idx} className={`source-card ${type}`}>
                <div className="source-header">
                  <span className="source-section">{source.section}</span>
                  {source.subsection && (
                    <span className="source-subsection">{source.subsection}</span>
                  )}
                  <span className="source-score">{(source.score * 100).toFixed(0)}%</span>
                </div>
                <p className="source-text">{source.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderGaps = (gaps: string[], messageId: string) => {
    const sectionId = `${messageId}-gaps`;
    const isExpanded = expandedSections.has(sectionId);

    return (
      <div className="advanced-gaps-section">
        <button
          className="sources-toggle gaps-toggle"
          onClick={() => toggleSection(sectionId)}
        >
          <span className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
          Compliance Gaps Identified ({gaps.length})
        </button>
        {isExpanded && (
          <ul className="gaps-list">
            {gaps.map((gap, idx) => (
              <li key={idx} className="gap-item">{gap}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderSteps = (steps: string[], messageId: string) => {
    const sectionId = `${messageId}-steps`;
    const isExpanded = expandedSections.has(sectionId);

    return (
      <div className="advanced-steps-section">
        <button
          className="sources-toggle steps-toggle"
          onClick={() => toggleSection(sectionId)}
        >
          <span className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
          Pipeline Steps ({steps.length})
        </button>
        {isExpanded && (
          <ol className="steps-list">
            {steps.map((step, idx) => (
              <li key={idx} className="step-item">{step}</li>
            ))}
          </ol>
        )}
      </div>
    );
  };

  return (
    <div className="advanced-chat">
      <div className="advanced-chat-messages">
        {messages.length === 0 && (
          <div className="advanced-chat-empty">
            <p>Ask a question about healthcare compliance.</p>
            <p className="empty-hint">
              This system cross-references hospital policies against federal regulations
              (HIPAA, CMS, FDA) to identify compliance gaps.
            </p>
          </div>
        )}
        {messages.map((message) => (
          <div key={message.id} className={`advanced-message ${message.role}`}>
            <div className="message-content">
              {message.content}
            </div>
            {message.role === 'assistant' && message.rewrittenQuery && (
              <div className="rewritten-query">
                <strong>Enhanced query:</strong> {message.rewrittenQuery}
              </div>
            )}
            {message.role === 'assistant' && message.gapsIdentified && message.gapsIdentified.length > 0 && (
              renderGaps(message.gapsIdentified, message.id)
            )}
            {message.role === 'assistant' && message.hospitalSources && message.hospitalSources.length > 0 && (
              renderSources(message.hospitalSources, 'Hospital Policy Sources', message.id, 'hospital')
            )}
            {message.role === 'assistant' && message.regulationSources && message.regulationSources.length > 0 && (
              renderSources(message.regulationSources, 'Federal Regulation Sources', message.id, 'regulation')
            )}
            {message.role === 'assistant' && message.steps && message.steps.length > 0 && (
              renderSteps(message.steps, message.id)
            )}
          </div>
        ))}
        {isLoading && (
          <div className="advanced-message assistant">
            <div className="message-content loading">
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              Performing cross-domain analysis...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSubmit} className="advanced-chat-input">
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Ask about compliance (e.g., 'Is our records retention policy HIPAA compliant?')"
          disabled={isLoading}
          rows={2}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        <button type="submit" disabled={isLoading || !inputMessage.trim()}>
          {isLoading ? 'Analyzing...' : 'Analyze'}
        </button>
      </form>
    </div>
  );
};

export default AdvancedChat;
